import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { resolveLanguage } from '@/application/i18n/language'
import { investigationSlug } from '@/application/seo/investigation-url'
import { Badge } from '@/ui/components/common/Badge'
import { Button } from '@/ui/components/common/Button'
import { ConclusionStamp } from '@/ui/components/public/ConclusionStamp'
import { MarkdownView } from '@/ui/components/common/MarkdownView'
import type { Investigation } from '@/domains/types'

/**
 * Card linking to a single investigation: cover image, categories, creation date,
 * title, description excerpt and optional author. Used inside the paginated list.
 * Shows a large circular conclusion badge on the bottom-right when a conclusion is set.
 */
export function InvestigationCard({ investigation }: { investigation: Investigation }) {
  const { t, i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)
  return (
    <div className="relative bg-background border border-border rounded-lg overflow-hidden transition-all duration-200 group-hover:border-primary/50 group-hover:shadow-lg group-hover:shadow-primary/5 flex group">
      <div className="w-40 sm:w-52 md:w-64 shrink-0 overflow-hidden bg-muted">
        <img
          src={investigation.imageUrl || '/logo.png'}
          alt={investigation.name}
          className={`w-full h-full transition-transform duration-300 group-hover:scale-105 ${
            investigation.imageUrl ? 'object-cover' : 'object-contain p-4 opacity-40'
          }`}
        />
      </div>

      <div
        className={`flex-1 p-5 border-l-2 border-l-primary/60 min-w-0 flex flex-col${investigation.conclusion && investigation.conclusion !== 'completed' ? ' pr-36' : ''}`}
      >
        {investigation.categoryTerms && investigation.categoryTerms.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {investigation.categoryTerms.map((term) => (
              <Badge
                key={term.id}
                variant="outline"
                className="text-xs border-primary/30 text-primary shrink-0"
              >
                {term.label}
              </Badge>
            ))}
          </div>
        )}
        <h3 className="font-serif text-lg font-bold text-foreground line-clamp-2 mb-2">
          {investigation.name}
        </h3>
        <MarkdownView
          content={investigation.description}
          compact
          className="text-sm text-muted-foreground line-clamp-3 mb-4"
        />

        <div className="flex items-center justify-between mt-auto pt-1 gap-2 flex-wrap">
          {investigation.author ? (
            <p className="text-xs text-muted-foreground">
              {t('investigation_detail.by')}{' '}
              <span className="text-foreground/70">{investigation.author}</span>
            </p>
          ) : (
            <span />
          )}
          <Link
            to="/investigations/$slug"
            params={{ slug: investigationSlug(investigation, locale) }}
          >
            <Button size="sm" variant="ghost">
              {t('investigations.view_details')} →
            </Button>
          </Link>
        </div>
      </div>

      {/* Date — always anchored to the top-right corner of the card */}
      <span className="absolute top-5 right-5 text-xs text-muted-foreground">
        {new Date(investigation.createdAt).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })}
      </span>

      {/* Conclusion badge — certification stamp, bottom-right */}
      {investigation.conclusion && investigation.conclusion !== 'completed' && (
        <ConclusionStamp
          conclusion={investigation.conclusion}
          size={96}
          opaqueBackground
          className="absolute bottom-4 right-4"
        />
      )}
    </div>
  )
}
